import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { QuotaProvider, useQuota } from "./context/QuotaContext";
import Login from "./pages/Login";
import Builder from "./pages/Builder";
import Admin from "./pages/Admin";
import AdminUserPage from "./pages/AdminUserPage";
import Landing from "./pages/Landing";
import CreatorsLanding from "./pages/CreatorsLanding";
import { PrivacyPolicy, TermsOfService } from "./pages/Legal";
import HelpPage from "./pages/HelpPage";
import Docs from "./pages/Docs";
import Paywall from "./pages/Paywall";
import ChannelConfigPage from "./pages/ChannelConfigPage";
import { Loader2 } from "lucide-react";
import PaymentDueBanner from "./components/PaymentDueBanner";
import MarketingTracker from "./components/MarketingTracker";
import MetaMarketingConsent from "./components/MetaMarketingConsent";

// `soloPlan={false}` para las pantallas que deben seguir siendo accesibles sin plan (soporte,
// admin). El resto queda tras el muro: sin plan activo no hay aplicación.
function Protected({ children, soloPlan = true }) {
  const { user } = useAuth();
  const { quota } = useQuota();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={48} />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  // El backend bloquea igual (ver _RUTAS_SIN_PLAN en get_current_user); esto solo evita que el
  // usuario vea una aplicación que le devolvería 402 en cada petición. Se espera a tener la cuota
  // para no enseñar el muro un instante a quien sí tiene plan.
  // El backend determina qué créditos siguen siendo gastables, incluido el saldo
  // pagado conservado tras un impago. Se permite entrar hasta agotarlo.
  if (soloPlan && quota && quota.plan === "gratis" && !quota.has_spendable_credits && !(quota.topup_seconds > 0) && user.role !== "admin") {
    return <><PaymentDueBanner /><Paywall /></>;
  }
  return <><PaymentDueBanner />{children}</>;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/app" replace /> : <Landing />} />
      <Route path="/creators" element={user ? <Navigate to="/app" replace /> : <CreatorsLanding />} />
      <Route path="/login" element={user ? <Navigate to="/app" replace /> : <Login />} />
      {/* Documentacion publica: sin Protected a proposito, tambien sirve de escaparate. */}
      <Route path="/docs" element={<Docs />} />
      <Route path="/docs/:seccion/:pagina" element={<Docs />} />
      <Route path="/privacidad" element={<PrivacyPolicy />} />
      <Route path="/terminos" element={<TermsOfService />} />
      <Route path="/app" element={<Protected><Builder /></Protected>} />
      <Route path="/app/config" element={<Protected><Builder /></Protected>} />
      <Route path="/app/library" element={<Protected><Builder /></Protected>} />
      <Route path="/app/referidos" element={<Protected><Builder /></Protected>} />
      <Route path="/app/channels/:channelId" element={<Protected><Builder /></Protected>} />
      <Route path="/app/channels/:channelId/videos/:projectId" element={<Protected><Builder /></Protected>} />
      <Route path="/app/channels/:channelId/videos/:projectId/:tab" element={<Protected><Builder /></Protected>} />
      <Route path="/admin" element={<Protected soloPlan={false}><Admin /></Protected>} />
      <Route path="/admin/users/:uid" element={<Protected soloPlan={false}><AdminUserPage /></Protected>} />
      <Route path="/ayuda" element={<Protected soloPlan={false}><HelpPage /></Protected>} />
      <Route path="/app/channels/:id/config" element={<Protected><ChannelConfigPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-center" richColors closeButton toastOptions={{ style: { fontSize: "16px" } }} />
            <QuotaProvider>
              <MarketingTracker />
              <MetaMarketingConsent />
              <AppRoutes />
            </QuotaProvider>
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
